/**
 * One totals row per distinct tax for the Services section of an invoice
 * (SPEC-christa-contacts-identity R3 AC1/AC2, production finding g).
 *
 * Decided PER LINE. A line with per-tax detail (`taxes`, attached by the backend) contributes each of its taxes under
 * its own code. A line without it, or whose detail no longer adds up to the line's own `tax_amount` (a cached row whose
 * amount was just edited), contributes its own `tax_rate` / `tax_amount` under GST. So a line is never dropped from the
 * GST row because its neighbours carry detail and it does not, which is what happened right after "Add Selected".
 */
export interface LineTaxDetail {
  tax_code?: string | null;
  label?: string | null;
  rate?: number | string | null;
  base_fraction?: number | string | null;
  amount?: number | string | null;
}

export interface TaxableLine {
  tax_rate?: number | string | null;
  tax_amount?: number | string | null;
  taxes?: LineTaxDetail[] | null;
}

export interface ServiceTaxRow {
  code: string;
  label: string;
  rate: number | null;
  baseFraction: number | null;
  amount: number;
}

const finiteOrNull = (value: unknown): number | null => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(n) ? n : null;
};

export function computeServiceTaxRows(lines: TaxableLine[]): ServiceTaxRow[] {
  const byCode = new Map<string, ServiceTaxRow>();
  const add = (code: string, label: string, rate: number | null, baseFraction: number | null, amount: number) => {
    if (!amount) return; // never a zero-amount tax row
    const existing = byCode.get(code);
    if (existing) {
      existing.amount += amount;
    } else {
      byCode.set(code, { code, label, rate, baseFraction, amount });
    }
  };

  for (const line of lines) {
    const lineTax = Number(line.tax_amount) || 0;
    const detail = Array.isArray(line.taxes) ? line.taxes : [];
    const detailTotal = detail.reduce((sum, tax) => sum + (Number(tax.amount) || 0), 0);
    // Detail that does not add up to the line's own tax is out of date (the cached row was edited); trust the line.
    const detailIsCurrent = detail.length > 0 && Math.abs(detailTotal - lineTax) < 0.005;
    if (detailIsCurrent) {
      for (const tax of detail) {
        const code = tax.tax_code || 'GST';
        add(code, tax.label || code, finiteOrNull(tax.rate), finiteOrNull(tax.base_fraction), Number(tax.amount) || 0);
      }
    } else if (lineTax > 0) {
      // Group by this line's OWN recorded rate -- never another line's rate, never a hardcoded 5.
      add('GST', 'GST', finiteOrNull(line.tax_rate), null, lineTax);
    }
  }

  return Array.from(byCode.values()).sort((a, b) =>
    a.code === 'GST' ? -1 : b.code === 'GST' ? 1 : a.code.localeCompare(b.code),
  );
}
